import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import api from '../../services/api';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import {
    FaFileInvoiceDollar, FaCalculator, FaCheckCircle,
    FaSpinner, FaUsers, FaChartLine, FaLandmark, FaArrowDown, FaArrowUp,
    FaSearch, FaMoneyBillWave, FaExclamationCircle, FaLockOpen, FaEdit,
    FaCalendarAlt, FaFileContract, FaTimes, FaPlus, FaTrash, FaFileDownload,
    FaHistory, FaIdBadge, FaMinusCircle, FaPlusCircle, FaInfoCircle, FaCoins,
    FaReceipt, FaChevronLeft, FaChevronRight
} from 'react-icons/fa';

import './FolhaPagamento.css';

// ============================================================================
// CONSTANTES — precisam bater 1:1 com o que routesRH.js espera no POST /eventos.
// O cálculo no backend soma dinamicamente em `total_${categoria}` (provento)
// ou `desconto_${categoria}` (desconto) — categorias fora dessa lista caem
// silenciosamente em "outros", então mantenha isso sincronizado com o backend.
// ============================================================================
const CATEGORIAS_PROVENTO = [
    { valor: 'horas_extras', label: 'Horas extras' },
    { valor: 'bonus', label: 'Bônus / Comissão' },
    { valor: 'beneficios', label: 'Benefícios' },
    { valor: 'outros_proventos', label: 'Outros proventos' },
];
const CATEGORIAS_DESCONTO = [
    { valor: 'faltas', label: 'Faltas' },
    { valor: 'beneficios', label: 'Coparticipação de benefícios' },
    { valor: 'outros', label: 'Outros descontos' },
];

const MESES_TREND = 6;

function competenciaAnterior(competencia, quantidade) {
    const [ano, mes] = competencia.split('-').map(Number);
    const data = new Date(ano, mes - 1 - quantidade, 1);
    return `${data.getFullYear()}-${String(data.getMonth() + 1).padStart(2, '0')}`;
}

function nomeMesCurto(competencia) {
    const [ano, mes] = competencia.split('-').map(Number);
    return new Date(ano, mes - 1, 2).toLocaleString('pt-BR', { month: 'short' }).replace('.', '');
}

// --- Sub-componente de KPI ---
const KpiCard = ({ icon, title, value, isLoading, colorClass, isCurrency = true, hint }) => {
    const formattedValue = (typeof value === 'number' && isCurrency)
        ? value.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
        : value;

    return (
        <div className={`fp-kpi-card ${colorClass || ''}`}>
            <div className="fp-kpi-icon-wrapper">
                {isLoading ? <FaSpinner className="fp-icon-spin" /> : icon}
            </div>
            <div className="fp-kpi-info">
                <span className="fp-kpi-label">
                    {title}
                    {hint && <span className="fp-kpi-hint" title={hint}><FaInfoCircle /></span>}
                </span>
                <span className="fp-kpi-value">{isLoading ? '...' : formattedValue}</span>
            </div>
        </div>
    );
};

// --- IDEIA NOVA #1: mini tendência dos últimos meses, só com a API existente ---
const TendenciaMensal = ({ competencia, versao }) => {
    const [pontos, setPontos] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        let cancelado = false;
        const buscarTendencia = async () => {
            setLoading(true);
            const competencias = Array.from({ length: MESES_TREND }, (_, i) => competenciaAnterior(competencia, MESES_TREND - 1 - i));
            try {
                const respostas = await Promise.all(
                    competencias.map(comp => api.get(`/api/rh/folha-pagamento/${comp}`).catch(() => null))
                );
                if (cancelado) return;
                const dados = respostas.map((r, i) => ({
                    competencia: competencias[i],
                    label: nomeMesCurto(competencias[i]),
                    valor: r?.data?.kpis?.totalLiquido || 0,
                    status: r?.data?.kpis?.status || 'aberta',
                }));
                setPontos(dados);
            } finally {
                if (!cancelado) setLoading(false);
            }
        };
        buscarTendencia();
        return () => { cancelado = true; };
    }, [competencia, versao]);

    const maiorValor = Math.max(1, ...pontos.map(p => p.valor));

    return (
        <div className="fp-trend-card">
            <div className="fp-trend-header">
                <FaHistory /> <span>Líquido pago — últimos {MESES_TREND} meses</span>
            </div>
            {loading ? (
                <div className="fp-trend-loading"><FaSpinner className="fp-icon-spin" /> Carregando tendência...</div>
            ) : (
                <div className="fp-trend-bars">
                    {pontos.map(p => (
                        <div key={p.competencia} className="fp-trend-bar-wrapper" title={`${p.label}: ${p.valor.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}`}>
                            <div
                                className={`fp-trend-bar ${p.competencia === competencia ? 'current' : ''} ${p.status}`}
                                style={{ height: `${Math.max(6, (p.valor / maiorValor) * 100)}%` }}
                            />
                            <span className="fp-trend-bar-label">{p.label}</span>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

// --- IDEIA NOVA #2: drawer de holerite detalhado + lançamento de eventos ---
const HoleriteDrawer = ({ item, competencia, folhaFechada, onClose, onEventoLancado }) => {
    const [eventos, setEventos] = useState([]);
    const [loadingEventos, setLoadingEventos] = useState(true);
    const [showForm, setShowForm] = useState(false);
    const [novoEvento, setNovoEvento] = useState({ tipo: 'provento', categoria: 'bonus', descricao: '', valor: '' });
    const [salvando, setSalvando] = useState(false);

    const carregarEventos = useCallback(async () => {
        setLoadingEventos(true);
        try {
            const { data } = await api.get(`/api/rh/folha-pagamento/eventos/${item.funcionario_id}/${competencia}`);
            setEventos(Array.isArray(data) ? data : []);
        } catch {
            toast.error('Erro ao carregar eventos deste colaborador.');
        } finally {
            setLoadingEventos(false);
        }
    }, [item.funcionario_id, competencia]);

    useEffect(() => { carregarEventos(); }, [carregarEventos]);

    const categoriasDisponiveis = novoEvento.tipo === 'provento' ? CATEGORIAS_PROVENTO : CATEGORIAS_DESCONTO;

    const handleTipoChange = (tipo) => {
        const categoriaPadrao = tipo === 'provento' ? CATEGORIAS_PROVENTO[0].valor : CATEGORIAS_DESCONTO[0].valor;
        setNovoEvento(f => ({ ...f, tipo, categoria: categoriaPadrao }));
    };

    const handleSalvarEvento = async (e) => {
        e.preventDefault();
        const valorNum = parseFloat(novoEvento.valor);
        if (!valorNum || valorNum <= 0) return toast.warn('Informe um valor válido.');

        setSalvando(true);
        try {
            const { data } = await api.post('/api/rh/folha-pagamento/eventos', {
                funcionario_id: item.funcionario_id,
                competencia,
                tipo: novoEvento.tipo,
                categoria: novoEvento.categoria,
                descricao: novoEvento.descricao || null,
                valor: valorNum,
            });
            toast.info(data.message || 'Evento lançado. Recalcule a folha para aplicar.');
            setNovoEvento({ tipo: 'provento', categoria: 'bonus', descricao: '', valor: '' });
            setShowForm(false);
            await carregarEventos();
            onEventoLancado();
        } catch (err) {
            toast.error(err.response?.data?.error || 'Erro ao lançar evento.');
        } finally {
            setSalvando(false);
        }
    };

    const handleExcluirEvento = async (eventoId) => {
        if (!window.confirm('Remover este evento? A folha voltará ao status "Aberta".')) return;
        try {
            await api.delete(`/api/rh/folha-pagamento/eventos/${eventoId}`);
            toast.success('Evento removido. Recalcule a folha.');
            await carregarEventos();
            onEventoLancado();
        } catch {
            toast.error('Erro ao remover evento.');
        }
    };

    const formatCurrency = (v) => (parseFloat(v) || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

    return (
        <div className="fp-drawer-overlay" onClick={onClose}>
            <div className="fp-drawer" onClick={e => e.stopPropagation()}>
                <div className="fp-drawer-header">
                    <div>
                        <span className="fp-drawer-eyebrow">Holerite — {competencia}</span>
                        <h2>{item.nome_funcionario}</h2>
                        <span className="fp-drawer-subtitle">Mat. {item.matricula || '---'} · {item.cargo || 'Cargo não informado'}</span>
                    </div>
                    <button className="fp-drawer-close" onClick={onClose}><FaTimes /></button>
                </div>

                <div className="fp-drawer-body">
                    <div className="fp-holerite-summary">
                        <div className="fp-holerite-line">
                            <span>Salário base</span>
                            <strong>{formatCurrency(item.salario_base)}</strong>
                        </div>
                        <div className="fp-holerite-line success">
                            <span><FaPlusCircle /> Proventos adicionais</span>
                            <strong>+{formatCurrency(item.total_proventos)}</strong>
                        </div>
                        <div className="fp-holerite-line danger">
                            <span><FaMinusCircle /> INSS</span>
                            <strong>-{formatCurrency(item.desconto_inss)}</strong>
                        </div>
                        <div className="fp-holerite-line danger">
                            <span><FaMinusCircle /> IRRF</span>
                            <strong>-{formatCurrency(item.desconto_irrf)}</strong>
                        </div>
                        <div className="fp-holerite-line danger">
                            <span><FaMinusCircle /> Empréstimos</span>
                            <strong>-{formatCurrency(item.desconto_emprestimos)}</strong>
                        </div>
                        <div className="fp-holerite-line danger">
                            <span><FaMinusCircle /> Outros descontos</span>
                            <strong>-{formatCurrency((item.total_descontos || 0) - (item.desconto_inss || 0) - (item.desconto_irrf || 0) - (item.desconto_emprestimos || 0))}</strong>
                        </div>
                        <div className="fp-holerite-line total">
                            <span>Líquido a receber</span>
                            <strong>{formatCurrency(item.total_liquido_calculado)}</strong>
                        </div>
                    </div>

                    <div className="fp-drawer-section">
                        <div className="fp-drawer-section-header">
                            <h3><FaReceipt /> Eventos lançados na competência</h3>
                            {!folhaFechada && (
                                <button className="fp-btn-mini" onClick={() => setShowForm(s => !s)}>
                                    <FaPlus /> {showForm ? 'Cancelar' : 'Lançar evento'}
                                </button>
                            )}
                        </div>

                        {showForm && (
                            <form className="fp-evento-form" onSubmit={handleSalvarEvento}>
                                <div className="fp-evento-tipo-toggle">
                                    <button type="button" className={novoEvento.tipo === 'provento' ? 'active provento' : ''} onClick={() => handleTipoChange('provento')}>
                                        <FaPlusCircle /> Provento
                                    </button>
                                    <button type="button" className={novoEvento.tipo === 'desconto' ? 'active desconto' : ''} onClick={() => handleTipoChange('desconto')}>
                                        <FaMinusCircle /> Desconto
                                    </button>
                                </div>
                                <select value={novoEvento.categoria} onChange={e => setNovoEvento(f => ({ ...f, categoria: e.target.value }))}>
                                    {categoriasDisponiveis.map(c => <option key={c.valor} value={c.valor}>{c.label}</option>)}
                                </select>
                                <input
                                    type="text" placeholder="Descrição (opcional)"
                                    value={novoEvento.descricao}
                                    onChange={e => setNovoEvento(f => ({ ...f, descricao: e.target.value }))}
                                />
                                <input
                                    type="number" step="0.01" placeholder="Valor (R$)"
                                    value={novoEvento.valor}
                                    onChange={e => setNovoEvento(f => ({ ...f, valor: e.target.value }))}
                                    required
                                />
                                <button type="submit" className="fp-btn-mini confirm" disabled={salvando}>
                                    {salvando ? <FaSpinner className="fp-icon-spin" /> : <FaCheckCircle />} Salvar
                                </button>
                            </form>
                        )}

                        {loadingEventos ? (
                            <p className="fp-drawer-empty"><FaSpinner className="fp-icon-spin" /> Carregando eventos...</p>
                        ) : eventos.length === 0 ? (
                            <p className="fp-drawer-empty">Nenhum evento manual lançado nesta competência.</p>
                        ) : (
                            <ul className="fp-evento-list">
                                {eventos.map(ev => (
                                    <li key={ev.id} className={`fp-evento-item ${ev.tipo}`}>
                                        <div>
                                            <span className="fp-evento-categoria">{ev.categoria.replace(/_/g, ' ')}</span>
                                            {ev.descricao && <span className="fp-evento-descricao"> · {ev.descricao}</span>}
                                        </div>
                                        <div className="fp-evento-actions">
                                            <strong className={ev.tipo === 'provento' ? 'text-success' : 'text-danger'}>
                                                {ev.tipo === 'provento' ? '+' : '-'}{formatCurrency(ev.valor)}
                                            </strong>
                                            {!folhaFechada && (
                                                <button onClick={() => handleExcluirEvento(ev.id)} title="Remover"><FaTrash /></button>
                                            )}
                                        </div>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default function FolhaPagamento() {
    const navigate = useNavigate();

    const storedUser = JSON.parse(localStorage.getItem('user')) || {};
    const isAdmin = storedUser.perfil === 'admin_geral' || storedUser.perfil === 'admin';

    const [competencia, setCompetencia] = useState(new Date().toISOString().slice(0, 7));
    const [itensFolha, setItensFolha] = useState([]);
    const [loading, setLoading] = useState(true);
    const [calculating, setCalculating] = useState(false);
    const [closing, setClosing] = useState(false);
    const [reopening, setReopening] = useState(false);
    const [search, setSearch] = useState('');

    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 10;

    const [showStamp, setShowStamp] = useState(false);
    const [stampText, setStampText] = useState('');

    const [selectedItem, setSelectedItem] = useState(null);
    const [refreshVersao, setRefreshVersao] = useState(0);

    const [kpis, setKpis] = useState({
        status: 'aberta',
        totalBruto: 0,
        totalDescontos: 0,
        totalLiquido: 0,
        totalCustoEmpresa: 0
    });

    const formatCurrency = (value) => (value || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

    const fetchFolhaData = useCallback(async (comp) => {
        setLoading(true);
        try {
            const { data } = await api.get(`/api/rh/folha-pagamento/${comp}`);
            setKpis(data.kpis || { status: 'aberta', totalBruto: 0, totalDescontos: 0, totalLiquido: 0, totalCustoEmpresa: 0 });
            setItensFolha(data.itens || []);
        } catch (error) {
            console.error("Erro ao buscar folha:", error);
            setKpis({ status: 'aberta', totalBruto: 0, totalDescontos: 0, totalLiquido: 0, totalCustoEmpresa: 0 });
            setItensFolha([]);
            toast.error("Falha ao sincronizar dados da folha.");
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { fetchFolhaData(competencia); setCurrentPage(1); }, [competencia, fetchFolhaData]);

    const triggerStamp = (text) => {
        setStampText(text);
        setShowStamp(true);
        setTimeout(() => setShowStamp(false), 2600);
    };

    const handleCalcularFolha = async (silent = false) => {
        if (!silent && !window.confirm(`Confirma o CÁLCULO da folha de ${competencia}?`)) return;

        setCalculating(true);
        try {
            await api.post('/api/rh/folha-pagamento/calcular', { competencia });
            await fetchFolhaData(competencia);
            setRefreshVersao(v => v + 1);

            if (!silent) {
                triggerStamp("CALCULADO");
                toast.success("Cálculo processado com sucesso!");
            }
        } catch (error) {
            toast.error(error.response?.data?.error || "Erro ao calcular a folha.");
        } finally {
            setCalculating(false);
        }
    };

    const handleFecharFolha = async () => {
        if (itensFolha.length === 0) return toast.warn("Calcule a folha antes de fechar.");
        const confirmMsg = `ATENÇÃO: O fechamento é definitivo e integra à conta 4.1.01.01. Deseja prosseguir?`;
        if (!window.confirm(confirmMsg)) return;

        setClosing(true);
        try {
            await api.post('/api/rh/folha-pagamento/fechar', { competencia });
            await fetchFolhaData(competencia);
            setRefreshVersao(v => v + 1);
            triggerStamp("FOLHA FECHADA");
            toast.success("Folha integrada ao financeiro!");
        } catch (error) {
            toast.error(error.response?.data?.error || "Erro ao processar fechamento.");
        } finally {
            setClosing(false);
        }
    };

    const handleReabrirFolha = async () => {
        if (!isAdmin) return;
        const confirmacao = window.prompt("Ação Crítica: Digite 'REABRIR' para liberar a folha:");
        if (confirmacao !== 'REABRIR') return;

        setReopening(true);
        try {
            await api.post(`/api/rh/folha-pagamento/reabrir`, { competencia });
            toast.success("Folha reaberta com sucesso!");
            await fetchFolhaData(competencia);
            setRefreshVersao(v => v + 1);
        } catch (err) {
            toast.error("Falha ao reabrir folha.");
        } finally {
            setReopening(false);
        }
    };

    const handleIrParaFinanceiro = () => {
        navigate(`/financeiro/contas-a-pagar?origem=folha_pagamento&competencia=${competencia}`);
    };

    // --- IDEIA NOVA #3: exportação CSV do fechamento, sem dependência extra ---
    const handleExportarCSV = () => {
        if (itensFolha.length === 0) return toast.warn('Nada para exportar nesta competência.');

        const cabecalho = ['Matrícula', 'Colaborador', 'Cargo', 'Salário Base', 'Proventos', 'INSS', 'IRRF', 'Empréstimos', 'Descontos Totais', 'Líquido'];
        const linhas = itensFolha.map(i => [
            i.matricula || '---', i.nome_funcionario, i.cargo || '', i.salario_base,
            i.total_proventos, i.desconto_inss, i.desconto_irrf, i.desconto_emprestimos,
            i.total_descontos, i.total_liquido_calculado
        ]);

        const escapar = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`;
        const csv = [cabecalho, ...linhas].map(linha => linha.map(escapar).join(';')).join('\n');
        const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `folha_pagamento_${competencia}.csv`;
        document.body.appendChild(link);
        link.click();
        link.remove();
        URL.revokeObjectURL(url);
        toast.success('Planilha exportada.');
    };

    const filteredItens = useMemo(() => {
        return itensFolha.filter(item =>
            item.nome_funcionario?.toLowerCase().includes(search.toLowerCase())
        );
    }, [itensFolha, search]);

    const paginatedItens = useMemo(() => {
        const start = (currentPage - 1) * itemsPerPage;
        return filteredItens.slice(start, start + itemsPerPage);
    }, [filteredItens, currentPage]);

    const totalPages = Math.max(1, Math.ceil(filteredItens.length / itemsPerPage));
    const folhaFechada = kpis.status === 'fechada' || kpis.status === 'paga';

    return (
        <div className={`fp-modern-layout ${showStamp ? 'fp-hit-shake' : ''}`}>

            {showStamp && (
                <div className="fp-premium-stamp">
                    <div className="fp-stamp-slam">
                        <div className={`fp-stamp-inner ${stampText === 'FOLHA FECHADA' ? 'st-danger' : 'st-success'}`}>
                            <span className="fp-stamp-text">{stampText}</span>
                            <span className="fp-stamp-sub">{competencia}</span>
                        </div>
                    </div>
                </div>
            )}

            <header className="fp-main-header">
                <div className="fp-brand-container">
                    <div className="fp-brand-icon"><FaFileInvoiceDollar /></div>
                    <div className="fp-brand-text">
                        <h1>Folha de Pagamento</h1>
                        <p>Gestão de proventos e integração contábil <b>(Conta 4.1.01.01)</b></p>
                    </div>
                </div>
                <div className="fp-header-meta">
                    <div className={`fp-status-pill ${kpis.status}`}>
                        {kpis.status === 'fechada' ? 'Lançada no Financeiro' : kpis.status.toUpperCase()}
                    </div>
                    {isAdmin && folhaFechada && (
                        <button className="fp-btn-reopen" onClick={handleReabrirFolha} disabled={reopening}>
                            {reopening ? <FaSpinner className="fp-icon-spin" /> : <FaLockOpen />} Reabrir
                        </button>
                    )}
                </div>
            </header>

            <section className="fp-action-bar">
                <div className="fp-input-group">
                    <label className="fp-input-label"><FaCalendarAlt /> Competência de Referência</label>
                    <div className="fp-calendar-dynamic">
                        <input
                            type="month"
                            className="fp-input-month"
                            value={competencia}
                            onChange={(e) => setCompetencia(e.target.value)}
                            disabled={loading || calculating || closing}
                        />
                        <div className="fp-calendar-display">
                            {new Date(competencia + '-02').toLocaleString('pt-BR', { month: 'long', year: 'numeric' })}
                        </div>
                    </div>
                </div>

                <div className="fp-action-group">
                    <button className="fp-btn btn-export" onClick={handleExportarCSV} disabled={itensFolha.length === 0}>
                        <FaFileDownload /> Exportar CSV
                    </button>
                    {!folhaFechada ? (
                        <>
                            <button className="fp-btn btn-calc" onClick={() => handleCalcularFolha()} disabled={calculating}>
                                {calculating ? <FaSpinner className="fp-icon-spin" /> : <FaCalculator />} Calcular Folha
                            </button>
                            <button className="fp-btn btn-close" onClick={handleFecharFolha} disabled={closing || kpis.status !== 'calculada'}>
                                {closing ? <FaSpinner className="fp-icon-spin" /> : <FaCheckCircle />} Fechar & Integrar
                            </button>
                        </>
                    ) : (
                        <button className="fp-btn btn-finance" onClick={handleIrParaFinanceiro}>
                            <FaMoneyBillWave /> Acessar Lançamentos
                        </button>
                    )}
                </div>
            </section>

            <div className="fp-kpi-grid">
                <KpiCard icon={<FaUsers />} title="Colaboradores" value={itensFolha.length} isLoading={loading} colorClass="blue" isCurrency={false} />
                <KpiCard icon={<FaArrowUp />} title="Proventos Brutos" value={kpis.totalBruto} isLoading={loading} colorClass="green" />
                <KpiCard icon={<FaArrowDown />} title="Total Retenções" value={kpis.totalDescontos} isLoading={loading} colorClass="red" />
                <KpiCard icon={<FaLandmark />} title="Líquido a Pagar" value={kpis.totalLiquido} isLoading={loading} colorClass="purple" />
                <KpiCard
                    icon={<FaChartLine />} title="Custo Empresa" value={kpis.totalCustoEmpresa || '---'}
                    isLoading={loading} colorClass="dark" isCurrency={typeof kpis.totalCustoEmpresa === 'number' && kpis.totalCustoEmpresa > 0}
                    hint="Ainda não calculado pelo backend (encargos patronais / FGTS). Exibindo apenas o que a API retorna."
                />
            </div>

            <TendenciaMensal competencia={competencia} versao={refreshVersao} />

            <div className="fp-data-card">
                <div className="fp-card-header">
                    <div className="fp-card-title">
                        <FaFileContract />
                        <h2>Detalhamento da Folha</h2>
                    </div>
                    <div className="fp-search-bar">
                        <FaSearch />
                        <input
                            placeholder="Buscar por nome..."
                            value={search}
                            onChange={(e) => { setSearch(e.target.value); setCurrentPage(1); }}
                        />
                    </div>
                </div>

                <div className="fp-table-responsive">
                    <table className="fp-table">
                        <thead>
                            <tr>
                                <th>Colaborador</th>
                                <th>Cargo</th>
                                <th className="txt-right">Salário Base</th>
                                <th className="txt-right text-success">Proventos</th>
                                <th className="txt-right text-danger">Descontos</th>
                                <th className="txt-right font-bold">Líquido</th>
                                <th className="txt-center">Ações</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading ? (
                                <tr><td colSpan="7" className="fp-loading-row"><FaSpinner className="fp-icon-spin" /> Atualizando base...</td></tr>
                            ) : filteredItens.length === 0 ? (
                                <tr>
                                    <td colSpan="7" className="fp-empty-row">
                                        <FaExclamationCircle />
                                        <p>{itensFolha.length === 0 ? 'A folha ainda não foi calculada para este mês.' : 'Nenhum resultado para a busca.'}</p>
                                    </td>
                                </tr>
                            ) : paginatedItens.map(item => (
                                <tr key={item.id} className="fp-table-row" onClick={() => setSelectedItem(item)}>
                                    <td>
                                        <div className="fp-user-info">
                                            <div className="fp-avatar">{item.nome_funcionario?.substring(0, 2).toUpperCase()}</div>
                                            <div>
                                                <div className="fp-user-name">{item.nome_funcionario}</div>
                                                <div className="fp-user-id"><FaIdBadge /> Mat. {item.matricula || '---'}</div>
                                            </div>
                                        </div>
                                    </td>
                                    <td><span className="fp-badge">{item.cargo || 'N/A'}</span></td>
                                    <td className="txt-right">{formatCurrency(item.salario_base)}</td>
                                    <td className="txt-right text-success">+{formatCurrency(item.total_proventos)}</td>
                                    <td className="txt-right text-danger">-{formatCurrency(item.total_descontos)}</td>
                                    <td className="txt-right fp-txt-highlight">{formatCurrency(item.total_liquido_calculado)}</td>
                                    <td className="txt-center">
                                        <button
                                            className="fp-btn-adjust"
                                            onClick={(e) => { e.stopPropagation(); setSelectedItem(item); }}
                                        >
                                            <FaEdit /> Holerite
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>

                {!loading && filteredItens.length > 0 && totalPages > 1 && (
                    <div className="fp-pagination-container">
                        <span className="fp-pagination-info">Mostrando <strong>{paginatedItens.length}</strong> de <strong>{filteredItens.length}</strong></span>
                        <div className="fp-pagination-controls">
                            <button className="fp-pagination-btn" onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1}><FaChevronLeft /></button>
                            <span>Página {currentPage} de {totalPages}</span>
                            <button className="fp-pagination-btn" onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages}><FaChevronRight /></button>
                        </div>
                    </div>
                )}
            </div>

            {selectedItem && (
                <HoleriteDrawer
                    item={selectedItem}
                    competencia={competencia}
                    folhaFechada={folhaFechada}
                    onClose={() => setSelectedItem(null)}
                    onEventoLancado={() => handleCalcularFolha(true)}
                />
            )}
        </div>
    );
}